import { Card, Grid, Stack, Typography } from '@mui/material';
import { useForm } from 'react-hook-form';
import {
  SVCheckbox,
  SVEmailInput,
  SVForm,
  SVPasswordInput,
  SVPhoneInput,
  SVTextInput,
} from '../core';
import FormSubmissionButtons from '../FormSubmissionButtons';
import ProfileEditAvatarButton from '../profile/ProfileEditAvatarButton';
import { StaffFormValues } from '@/types/staff';
import { useAddStaff, useEditStaff } from '@/hooks/requests/useStaff';
import { MESSAGES } from '@/constants/messages';
import showToast from '@/utils/toast';
import { FORM_FIELDS } from '@/constants/formFields';
import { initialStaffForm } from '@/constants/staff';

type StaffFormProps = {
  isEdit?: boolean;
  defaultValues?: StaffFormValues;
  onLeave?: () => void;
  onSuccess: (uuid: string) => void;
};

export default function StaffForm({
  isEdit,
  defaultValues,
  onLeave,
  onSuccess,
}: StaffFormProps) {
  const { addStaff, isAddStaffPending } = useAddStaff();
  const { editStaff, isEditStaffPending } = useEditStaff();

  const formMethods = useForm<StaffFormValues>({
    defaultValues: defaultValues ? defaultValues : initialStaffForm,
    mode: 'onBlur',
  });
  const [isSandboxUser] = formMethods.watch(['isSandboxUser']);

  async function onSubmit(data: StaffFormValues) {
    try {
      if (isEdit) {
        await editStaff(data);
        showToast({
          type: 'success',
          data: { content: 'The staff member has been successfully edited' },
        });
        onSuccess(data.uuid);
      } else {
        const response = await addStaff(data);
        showToast({
          type: 'success',
          data: { content: 'The staff member has been successfully added' },
        });
        onSuccess(response);
      }
    } catch {
      showToast({ type: 'error', data: { content: MESSAGES.genericError } });
    }
  }

  return (
    <SVForm methods={formMethods} onSubmit={onSubmit}>
      <Stack spacing={2}>
        <Card>
          <Typography variant="h5" mb={2}>
            Basic Info
          </Typography>
          <Grid spacing={2} container>
            <Grid container size={{ xs: 12, md: 9 }}>
              <Grid size={{ xs: 12, md: 6 }}>
                <SVTextInput name="firstName" {...FORM_FIELDS.FIRST_NAME} />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <SVTextInput name="lastName" {...FORM_FIELDS.LAST_NAME} />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <SVTextInput
                  rules={{ required: true }}
                  label="Title"
                  name="title"
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <SVEmailInput
                  disabled={Boolean(defaultValues)}
                  name="email"
                  {...FORM_FIELDS.EMAIL}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <SVPhoneInput name="phoneNumber" {...FORM_FIELDS.PHONE} />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <SVPhoneInput
                  name="businessPhoneNumber"
                  {...FORM_FIELDS.BUSINESS_PHONE}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <SVCheckbox label="Is Sandbox User" name="isSandboxUser" />
              </Grid>
              {isSandboxUser && (
                <Grid size={{ xs: 12, md: 6 }}>
                  <SVPasswordInput
                    label="Sandbox Password"
                    name="sandboxPassword"
                  />
                </Grid>
              )}
              <Grid size={{ xs: 12 }}>
                <SVTextInput
                  multiline
                  multilineRows={2}
                  name="bio"
                  label="Bio"
                  rules={{ maxLength: 1000 }}
                />
              </Grid>
            </Grid>
            <Grid sx={{ textAlign: 'center' }} size={{ xs: 12, md: 3 }}>
              <ProfileEditAvatarButton
                uuid={defaultValues ? defaultValues.uuid : null}
              />
            </Grid>
          </Grid>
        </Card>
        <FormSubmissionButtons
          isLoading={isAddStaffPending || isEditStaffPending}
          onLeave={onLeave}
        />
      </Stack>
    </SVForm>
  );
}
